import { Component, type ErrorInfo, type ReactNode } from 'react';

import { RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';

type Props = {
  children: ReactNode;
  /** Replaces the default panel. Receives a reset that clears the error. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
};

type State = { error: Error | null };

/**
 * Catches a render error and shows a panel instead of a blank page.
 *
 * A class because there is still no hook equivalent (`componentDidCatch` is the only
 * way React exposes this).
 *
 * Deliberately does not catch errors from event handlers or async code; React does not
 * route those here. Those surface as failed queries and are handled by the view's own
 * error state.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The stack is the only record of this; console.error is allowed by the rule.
    console.error('Unhandled render error', error, info.componentStack);
  }

  private readonly reset = () => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <EmptyState
        tone="danger"
        title="This screen ran into a problem"
        body="The rest of the application is still working. Try again, or move to another screen."
        action={{ label: 'Try again', onClick: this.reset }}
      />
    );
  }
}

/** A full-page variant, for the boundary wrapped around the router itself. */
export function RootErrorFallback({ reset }: { reset: () => void }) {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minBlockSize: '100vh' }}>
      <EmptyState
        tone="danger"
        title="Flyst could not load"
        body="Something went wrong starting the application. Reloading usually clears it."
        action={{ label: 'Reload', onClick: reset }}
      />
      <Button
        variant="subtle"
        icon={<RotateCcw size={14} />}
        onClick={() => {
          window.location.reload();
        }}
      >
        Reload the page
      </Button>
    </div>
  );
}
